import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { TextContainerPosition } from 'graphql-ts-types';
import { position } from 'styled-system';
import type { PositionProps } from 'styled-system';

import { Flex } from 'common/components/Flex';
import type { FlexProps } from 'common/components/Flex';
import type { Color } from 'common/theme/colors/types';
import CampaignDuration from '../shared/components/content/CampaignDuration';
import CountDown from '../shared/components/content/CountDown';
import LegalDisclaimer from '../shared/components/content/LegalDisclaimer';
import LogoImage from '../shared/components/content/LogoImage';
import Subtitle from '../shared/components/content/Subtitle';
import Title from '../shared/components/content/Title';
import VoucherCode from '../shared/components/content/VoucherCode';
import type { BannerProps } from '../types';
import BannerCtaButton from './BannerCtaButton';

interface TextBoxContentProps extends Partial<BannerProps> {
  isStaticBackground?: boolean;
  onClick?: () => void;
  onCtaClick?: () => void;
  onVoucherClick?: () => void;
  isFloatingTextBox?: boolean;
}

const Container = styled(Flex)<FlexProps & PositionProps>`
  ${position};
`;

const containerStyles: ComponentProps<typeof Container> = {
  width: '100%',
  position: 'relative',
  flexDirection: 'column',
  gap: ['sp_16', 'sp_20', 'sp_16', 'sp_32'],
};

const getContainerAlignmentStyles = (isCentered?: boolean): ComponentProps<typeof Container> =>
  isCentered ? { alignItems: 'center', textAlign: 'center' } : {};

const TextBoxContent = ({
  title,
  subtitle,
  logoImage,
  countdownEndDate,
  campaignDuration,
  voucherCode,
  textColor,
  isMultiVoucher,
  ctaButtonLabel,
  ctaButtonAriaLabel,
  ctaDisclaimerContent,
  legalDisclaimerContent,
  isStaticBackground,
  onVoucherClick,
  textContainerPosition,
  isFloatingTextBox,
}: TextBoxContentProps) => {
  const isCentered = isFloatingTextBox && textContainerPosition === TextContainerPosition.Center;
  const alignmentStyles = getContainerAlignmentStyles(isCentered);

  return (
    <Container {...containerStyles} {...alignmentStyles}>
      <LogoImage logoImage={logoImage} />
      {(title || subtitle) && (
        <Flex {...containerStyles} gap="sp_8" {...alignmentStyles}>
          <Title title={title} textColor={textColor as Color} />
          <Subtitle subtitle={subtitle} textColor={textColor as Color} />
        </Flex>
      )}
      {(campaignDuration || countdownEndDate) && (
        <Flex {...containerStyles} gap="sp_4" {...alignmentStyles}>
          <CampaignDuration campaignDuration={campaignDuration} textColor={textColor as Color} />
          <CountDown countdownEndDate={countdownEndDate} textColor={textColor} />
        </Flex>
      )}
      <VoucherCode voucherCode={voucherCode} onClick={onVoucherClick} />
      <BannerCtaButton
        textColor={textColor}
        isMultiVoucher={isMultiVoucher}
        ctaButtonLabel={ctaButtonLabel}
        ctaButtonAriaLabel={ctaButtonAriaLabel}
        ctaDisclaimerContent={ctaDisclaimerContent}
      />
      <LegalDisclaimer
        isStaticBackground={isStaticBackground}
        legalDisclaimerContent={legalDisclaimerContent}
        {...(isStaticBackground ? { display: ['flex', null, 'none'] } : { display: 'none' })}
      />
    </Container>
  );
};

export default TextBoxContent;
